import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight, Loader2, Shield, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";

export default function Login() {
  const { login, bankid } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const emailParam = new URLSearchParams(location.search).get("email");
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const [email, setEmail] = useState(emailParam || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bankidLoading, setBankidLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Välkommen tillbaka");
      nav(safeNext || "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Inloggning misslyckades"));
    } finally {
      setLoading(false);
    }
  };

  const doBankid = async () => {
    setBankidLoading(true);
    // Simulate BankID waiting flow
    await new Promise((r) => setTimeout(r, 1200));
    try {
      await bankid();
      toast.success("Inloggad med BankID");
      nav(safeNext || "/dashboard");
    } catch {
      toast.error("BankID-inloggning misslyckades");
    } finally {
      setBankidLoading(false);
    }
  };

  const doGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (safeNext || "/dashboard");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <Seo
        title="Logga in"
        description="Logga in på ditt Bygglovskompassen-konto för att fortsätta med dina bygglovsärenden och spara pågående ansökningar."
        path="/login"
      />
      {/* Left: image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1583752582921-23c7d75c75e0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Modern arkitektur"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-900/40 mix-blend-multiply" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white flex items-center justify-center rounded-sm overflow-hidden">
              <img src="/logo-icon.png" alt="Bygglovskompassen" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <div className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</div>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Digital e-tjänst</div>
            </div>
          </div>
          <div className="space-y-4">
            <h1 className="text-5xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
              Hantera bygglov<br />enkelt och säkert.
            </h1>
            <p className="text-white/80 max-w-md leading-relaxed">
              Tjänsten för kommuner, företag och privatpersoner. Lämna in handlingar,
              följ status i realtid och kommunicera direkt med handläggare.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] opacity-70">
            Stockholms stad · Byggnadsnämnden
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo-icon.png" alt="Bygglovskompassen" className="w-10 h-10 object-contain" />
            <div>
              <div className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Digital e-tjänst</div>
            </div>
          </div>

          <h2 className="text-3xl font-semibold text-zinc-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
            Logga in
          </h2>
          <p className="mt-1 text-sm text-zinc-500">Välj inloggningssätt nedan.</p>

          {/* BankID */}
          <button
            type="button"
            data-testid="bankid-button"
            onClick={doBankid}
            disabled={bankidLoading}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-blue-700 hover:bg-blue-800 text-white font-medium py-3.5 rounded-sm transition active:scale-[0.98] disabled:opacity-60"
          >
            {bankidLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Starta BankID på enheten...</>
            ) : (
              <><Shield className="w-4 h-4 stroke-[1.5]" /> Logga in med BankID</>
            )}
          </button>

          <button
            type="button"
            data-testid="google-login-button"
            onClick={doGoogle}
            className="mt-3 w-full flex items-center justify-center gap-3 bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-900 font-medium py-3 rounded-sm transition active:scale-[0.98]"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Fortsätt med Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-400">
            <div className="flex-1 h-px bg-zinc-200" />
            <span>Eller med e-post</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 stroke-[1.5]" />
              <input
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="namn@exempel.se"
                className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm bg-white focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 stroke-[1.5]" />
              <input
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Lösenord"
                className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm bg-white focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-medium py-3 rounded-sm transition active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Logga in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-zinc-500 text-center">
            Saknar du konto?{" "}
            <Link to="/register" data-testid="link-register" className="text-blue-700 hover:underline font-medium">
              Skapa konto
            </Link>
          </p>

          <div className="mt-8 p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-xs text-zinc-600">
            <div className="font-semibold mb-1 text-zinc-700">Testkonton</div>
            <div>Handläggare: handlaggare@kommun.se / Handlaggare123!</div>
            <div>Privatperson: anna@example.se / Anna123!</div>
          </div>
        </div>
      </div>
    </div>
  );
}
